/* eslint-disable camelcase */
import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Autocomplete, Divider } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"
import DetalleArticulosGrid from "./components/DetalleArticulosGrid"
import { useQueryClient } from "@tanstack/react-query"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1300px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearSolicitudIngEgr = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    tsolInEg: "",
    tsolcodigo: "", // NUEVO: Clave primaria real para enviar y diferenciar
    ciaaproautinsol: 0,
    solorigen: "",
    tmotcodigo: "",
    sgadescri: "",
    clicodigo: "",
    procodigo: "",
    agencodigosol: "",
    agencodigoenv: "",
    condescrienv: "",
    locsolicita: "",
    locrecibe: "",
    bodegreso: "",
    bodingreso: "",
    integracodigo: "",
    proyectocodigo: "",
    detalle: [],
  })

  // ==========================================================================
  // Convertimos el código a MAYÚSCULAS para evitar fallos de evaluación
  // ==========================================================================
  const t = String(formData.tsolInEg || "").toUpperCase()

  // CASO 1: Visibilidad dinámica dependiendo del tipo de solicitud
  const showCliente = ["SOLINGGSGA", "SOLEGRGSGA", "SOLEGRPRES"].includes(t)
  const showProveedor = ["SOLINGGSGA", "SOLEGRGSGA"].includes(t)
  const showAgencias = t === "SOLEGRGSGA"
  const showLocSolicita = ["SOLEGRLOC", "SOLEGRPRES", "SOLEGRBOD", "SOLINGAJU", "SOLEGRAJU"].includes(t)
  const showLocRecibe = t === "SOLEGRLOC"
  const showBodEgreso = ["SOLEGRPRES", "SOLEGRBOD", "SOLEGRAJU"].includes(t)
  const showBodIngreso = ["SOLEGRBOD", "SOLINGAJU"].includes(t)
  const showIntegradorProyecto = t === "SOLEGRPRES"

  const { data: catData, isLoading: loadCatLog } = useQuery({
    queryKey: ["catalogosLogisticos", formData.clicodigo],
    queryFn: async () => {
      try {
        const payload = formData.clicodigo ? { clicodigo: formData.clicodigo } : {}
        const response = await api.post("/CrearSolicitudIngEgr/getCatalogosLogisticos", payload)

        let catalogos = {}
        const res = response?.data?.data || response?.data || response || {}

        if (res.localidades) {
          catalogos = res
        } else if (res.data && res.data.localidades) {
          catalogos = res.data
        } else if (response?.data?.data?.data?.localidades) {
          catalogos = response.data.data.data
        }

        return catalogos
      } catch (error) {
        return {}
      }
    },
    staleTime: 0,
  })

  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []

  const listaTiposSolicitud = extractArray(catData?.tipos_solicitud)
  const listaLocalidades = extractArray(catData?.localidades)
  const listaBodegas = extractArray(catData?.bodegas)
  const listaAgencias = extractArray(catData?.agencias)
  const listaClientes = extractArray(catData?.clientes)
  const listaProveedores = extractArray(catData?.proveedores)
  const listaMotivos = extractArray(catData?.motivos)
  const listaIntegradores = extractArray(catData?.integradores)
  const listaProyectos = extractArray(catData?.proyectos)

  const { mutateAsync: SaveSolicitud, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingSolicitud"],
    fn: async (data) => {
      const response = await api.post("/CrearSolicitudIngEgr/createSolicitud", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await queryClient.invalidateQueries()
      navigate(-1)
    },
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
  }

  const handleTipoSolicitudChange = (event, newValue) => {
    // Almacenamos tanto la Naturaleza (id) como el Código Primario (tsolcodigo)
    const nuevoTipo = newValue ? String(newValue.id) : ""
    const nuevoCodigo = newValue ? String(newValue.tsolcodigo) : ""
    const autoAprob = newValue?.ciaaproautinsol || 0

    setFormData((prev) => ({
      ...prev,
      tsolInEg: nuevoTipo,
      tsolcodigo: nuevoCodigo,
      ciaaproautinsol: autoAprob,
      solorigen: "",
      clicodigo: "",
      procodigo: "",
      agencodigosol: "",
      agencodigoenv: "",
      condescrienv: "",
      locsolicita: "",
      locrecibe: "",
      bodegreso: "",
      bodingreso: "",
      integracodigo: "",
      proyectocodigo: "",
      detalle: [],
    }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // 1. Validaciones Generales usando tsolcodigo para verificar selección completa
    if (!formData.tsolcodigo || !formData.tsolInEg) return showWarning("Debe seleccionar el Tipo de Solicitud.")
    if (!formData.tmotcodigo) return showWarning("Debe seleccionar un Motivo.")
    if (!formData.sgadescri.trim()) return showWarning("Debe ingresar una descripción u observación.")

    // 2. Validaciones Condicionales
    if (showCliente && !formData.clicodigo) return showWarning("Debe seleccionar un Cliente.")
    if (showProveedor && !formData.procodigo) return showWarning("Debe seleccionar un Proveedor.")
    if (showAgencias && (!formData.agencodigosol || !formData.agencodigoenv))
      return showWarning("Debe seleccionar las agencias que solicitan y reciben.")
    if (showLocSolicita && !formData.locsolicita) return showWarning("Debe seleccionar la Localidad que Solicita.")
    if (showLocRecibe && !formData.locrecibe) return showWarning("Debe seleccionar la Localidad a la que solicitan.")
    if (showBodEgreso && !formData.bodegreso) return showWarning("Debe seleccionar la Bodega de Egreso.")
    if (showBodIngreso && !formData.bodingreso) return showWarning("Debe seleccionar la Bodega de Ingreso.")

    // 3. Validaciones de Transferencias
    if (t === "SOLEGRBOD" && formData.bodegreso === formData.bodingreso) {
      return showWarning("La bodega de egreso y la bodega de ingreso no pueden ser la misma.")
    }
    if (t === "SOLEGRLOC" && formData.locsolicita === formData.locrecibe) {
      return showWarning("La localidad que solicita y la localidad a la que solicitan no pueden ser la misma.")
    }

    if (formData.detalle.length === 0) return showWarning("Debe agregar al menos un artículo al detalle.")

    await SaveSolicitud(formData)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((a) => a?.acccaption === "GRABAR")
  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  const loadingGlobal = isSaving || loadCatLog

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={loadingGlobal}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nueva Solicitud de Inventario</b>
        </div>

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de Cabecera
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaTiposSolicitud}
                  getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                  value={
                    listaTiposSolicitud.find((c) => String(c.tsolcodigo) === String(formData.tsolcodigo)) ||
                    (formData.tsolcodigo
                      ? { id: formData.tsolInEg, tsolcodigo: formData.tsolcodigo, label: "Cargando..." }
                      : null)
                  }
                  // CAMBIO CRUCIAL: Evaluamos por tsolcodigo para evitar el error de IDs duplicados en el Autocomplete
                  isOptionEqualToValue={(option, value) => String(option.tsolcodigo) === String(value?.tsolcodigo)}
                  onChange={handleTipoSolicitudChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Solicitud *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaMotivos}
                  getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                  value={
                    listaMotivos.find((c) => String(c.id) === String(formData.tmotcodigo)) ||
                    (formData.tmotcodigo ? { id: formData.tmotcodigo, label: "Cargando..." } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, tmotcodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Motivo *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción / Observación *"
                  value={formData.sgadescri}
                  onChange={(e) => handleInputChange("sgadescri", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>

              {showCliente && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaClientes}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaClientes.find((c) => String(c.id) === String(formData.clicodigo)) ||
                      (formData.clicodigo ? { id: formData.clicodigo, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) => {
                      setFormData((prev) => ({
                        ...prev,
                        clicodigo: newVal ? String(newVal.id) : "",
                        agencodigosol: "",
                        agencodigoenv: "",
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Cliente *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showProveedor && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaProveedores}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaProveedores.find((c) => String(c.id) === String(formData.procodigo)) ||
                      (formData.procodigo ? { id: formData.procodigo, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, procodigo: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Proveedor *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showAgencias && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      disabled={!formData.clicodigo}
                      options={listaAgencias}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaAgencias.find((c) => String(c.id) === String(formData.agencodigosol)) ||
                        (formData.agencodigosol ? { id: formData.agencodigosol, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(e, newVal) =>
                        setFormData((prev) => ({ ...prev, agencodigosol: newVal ? String(newVal.id) : "" }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Agencia que Solicita *" InputLabelProps={{ shrink: true }} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      disabled={!formData.clicodigo}
                      options={listaAgencias}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaAgencias.find((c) => String(c.id) === String(formData.agencodigoenv)) ||
                        (formData.agencodigoenv ? { id: formData.agencodigoenv, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(e, newVal) =>
                        setFormData((prev) => ({ ...prev, agencodigoenv: newVal ? String(newVal.id) : "" }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Agencia de Envío *" InputLabelProps={{ shrink: true }} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label="Contacto de Agencia de Envío"
                      value={formData.condescrienv}
                      onChange={(e) => handleInputChange("condescrienv", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 60 }}
                    />
                  </Grid>
                </>
              )}

              {showLocSolicita && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={listaLocalidades}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaLocalidades.find((c) => String(c.id) === String(formData.locsolicita)) ||
                      (formData.locsolicita ? { id: formData.locsolicita, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) => {
                      setFormData((prev) => ({
                        ...prev,
                        locsolicita: newVal ? String(newVal.id) : "",
                        bodegreso: "",
                        bodingreso: "",
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Localidad que Solicita *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showLocRecibe && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={listaLocalidades}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaLocalidades.find((c) => String(c.id) === String(formData.locrecibe)) ||
                      (formData.locrecibe ? { id: formData.locrecibe, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, locrecibe: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Localidad a la que solicitan *"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              )}

              {showBodEgreso && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    disabled={!formData.locsolicita}
                    options={listaBodegas.filter((b) => b.loccodigo === formData.locsolicita)}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaBodegas.find((c) => String(c.id) === String(formData.bodegreso)) ||
                      (formData.bodegreso ? { id: formData.bodegreso, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, bodegreso: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Bodega de Egreso *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showBodIngreso && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    disabled={!formData.locsolicita}
                    options={listaBodegas.filter((b) => b.loccodigo === formData.locsolicita)}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaBodegas.find((c) => String(c.id) === String(formData.bodingreso)) ||
                      (formData.bodingreso ? { id: formData.bodingreso, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, bodingreso: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Bodega de Ingreso *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showIntegradorProyecto && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={listaIntegradores}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaIntegradores.find((c) => String(c.id) === String(formData.integracodigo)) ||
                        (formData.integracodigo ? { id: formData.integracodigo, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(e, newVal) =>
                        setFormData((prev) => ({ ...prev, integracodigo: newVal ? String(newVal.id) : "" }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Integrador" InputLabelProps={{ shrink: true }} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={listaProyectos}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaProyectos.find((c) => String(c.id) === String(formData.proyectocodigo)) ||
                        (formData.proyectocodigo ? { id: formData.proyectocodigo, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(e, newVal) =>
                        setFormData((prev) => ({ ...prev, proyectocodigo: newVal ? String(newVal.id) : "" }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Proyecto" InputLabelProps={{ shrink: true }} />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Detalle de Artículos
            </Typography>

            {formData.tsolInEg ? (
              <DetalleArticulosGrid
                detalle={formData.detalle}
                setFormData={setFormData}
                tsolInEg={formData.tsolInEg}
                clicodigo={formData.clicodigo}
                loccodigo={formData.locsolicita || formData.locrecibe}
                bodcodigo={formData.bodegreso || formData.bodingreso}
              />
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", mt: 2 }}>
                Seleccione un Tipo de Solicitud para habilitar la carga de artículos.
              </Typography>
            )}
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearSolicitudIngEgr
